import { useCallback, useState } from 'react';
import {
  CRM_NAV_DOCK_PIN_DRAG_TYPE,
  buildNavDockDragPayload,
  parseNavDockDragPayload
} from '../utils/crmNavDockDragPin';

export function useCrmNavDockDragPin({ enabled = false, onPinPath } = {}) {
  const [draggingPath, setDraggingPath] = useState(null);
  const [isDockDropActive, setIsDockDropActive] = useState(false);

  const handleItemDragStart = useCallback(
    (item) => (event) => {
      if (!enabled || !item?.to) return;
      const payload = buildNavDockDragPayload(item);
      if (!payload) return;

      event.dataTransfer.effectAllowed = 'copy';
      event.dataTransfer.setData(CRM_NAV_DOCK_PIN_DRAG_TYPE, payload);
      event.dataTransfer.setData('text/plain', item.to);
      setDraggingPath(item.to);

      const source = event.currentTarget;
      if (source && typeof event.dataTransfer.setDragImage === 'function') {
        const ghost = source.cloneNode(true);
        ghost.style.position = 'fixed';
        ghost.style.top = '-9999px';
        ghost.style.left = '-9999px';
        ghost.style.width = `${Math.max(source.offsetWidth, 120)}px`;
        ghost.style.pointerEvents = 'none';
        ghost.classList.add('crm-nav-dock-drag-ghost');
        document.body.appendChild(ghost);
        event.dataTransfer.setDragImage(ghost, 36, 24);
        window.setTimeout(() => ghost.remove(), 0);
      }
    },
    [enabled]
  );

  const handleItemDragEnd = useCallback(() => {
    setDraggingPath(null);
    setIsDockDropActive(false);
  }, []);

  const handleDockDragEnter = useCallback(
    (event) => {
      if (!enabled) return;
      event.preventDefault();
      setIsDockDropActive(true);
    },
    [enabled]
  );

  const handleDockDragOver = useCallback(
    (event) => {
      if (!enabled) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
      setIsDockDropActive(true);
    },
    [enabled]
  );

  const handleDockDragLeave = useCallback((event) => {
    const next = event.relatedTarget;
    const current = event.currentTarget;
    if (next && current.contains(next)) return;
    setIsDockDropActive(false);
  }, []);

  const handleDockDrop = useCallback(
    (event) => {
      if (!enabled) return;
      event.preventDefault();
      const payload = parseNavDockDragPayload(event.dataTransfer);
      setDraggingPath(null);
      setIsDockDropActive(false);
      if (!payload?.path) return;
      onPinPath?.(payload.path, payload);
    },
    [enabled, onPinPath]
  );

  return {
    draggingPath,
    isDockDropActive,
    handleItemDragStart,
    handleItemDragEnd,
    dockDropZoneProps: enabled
      ? {
          onDragEnter: handleDockDragEnter,
          onDragOver: handleDockDragOver,
          onDragLeave: handleDockDragLeave,
          onDrop: handleDockDrop
        }
      : {}
  };
}
